import { cn } from "@/lib/utils";
import { ButtonShadcn } from "@/components/ui/Button";

/**
 * A visible two-or-more-way choice: a recessed track with the selected option
 * raised out of it. Use it where the options are few and the reader should see
 * what they are choosing between without opening anything — a scope choice, a
 * view toggle. A longer list belongs in `Select`.
 *
 * The selected option is a **raised chip**, not a filled one: the label sits on
 * `background-secondary` and clears 6.4:1 in light and 4.7:1 in dark. A
 * `bg-button-secondary/20` tint — the treatment the candidate tabs use — is only
 * 4.4:1 once it is laid over `background-primary`, which is what a recessed
 * track is made of. Same accent colour, one step more legible.
 *
 * `radiogroup` / `radio` rather than a row of plain buttons, so a screen reader
 * reads it as one choice with one answer, and the selection carries in ARIA and
 * in shape rather than in colour alone.
 *
 * Picking the option already selected fires nothing — the owner is usually
 * refetching on change, and a stray click on the current view should cost
 * nothing.
 *
 * `type="button"` on every option, said out loud even though `ButtonShadcn` now
 * defaults to it. A bare `<button>` inside a `<form>` is `type="submit"`, and
 * this control exists to be dropped into forms: in the note composer, picking a
 * scope once posted the half-written note. Stating it here means the option
 * stays a button even if that default is ever revisited.
 *
 * Two looks, one control. `default` is the recessed track above, sized to its
 * labels, for a choice sitting inside a page. `pill` is a full-width capsule on
 * a translucent track, for a choice that *is* the top of a panel — the login
 * page's sign-in method, over a photographic background where a solid track
 * would read as a second card.
 *
 * `chips` drops the track: each option is its own outlined pill and the row
 * wraps, for a longer set of short answers inside a form — the kind of a piece
 * of feedback, say. The selected pill is filled with the accent, the
 * `secondary` button's own pair: with no track to raise it out of, an accent
 * border and label alone read too close to the unselected pills around it.
 *
 * The selected chip of the two tracked looks is `bg-background-secondary`, never a fixed
 * white: white in both themes needs a label that is dark in both, and this app
 * has no such token — which is how the login page ended up with a white chip
 * carrying a white label under the dark theme.
 *
 * @param {{value: string, label: string, title?: string}[]} options
 * @param {string} value           the selected option's value
 * @param {Function} onChange      called with the newly picked value
 * @param {boolean} [disabled]     disables every option, e.g. while a request is in flight
 * @param {'default'|'pill'|'chips'} [variant]
 * @param {string} ariaLabel       what the whole choice is about, e.g. "Note scope"
 */
const VARIANTS = {
  default: {
    track: "inline-flex items-center gap-0.5 rounded-lg border border-border-primary bg-background-primary p-0.5",
    option: "rounded-md px-3",
    selected: "bg-background-secondary font-medium text-button-secondary shadow-sm hover:bg-background-secondary hover:text-button-secondary",
    unselected: "text-text-secondary hover:bg-transparent hover:text-text-primary",
  },
  pill: {
    track: "flex w-full rounded-full border border-white/20 bg-white/10 p-1 text-xs sm:text-sm",
    option: "h-auto flex-1 rounded-full py-1.5 transition-all",
    selected: "bg-background-secondary font-medium text-text-primary shadow-sm hover:bg-background-secondary hover:text-text-primary",
    unselected: "bg-transparent text-text-primary hover:bg-transparent hover:text-text-primary",
  },
  chips: {
    track: "flex flex-wrap gap-2",
    option: "h-auto rounded-full border px-4 py-1.5 text-sm",
    selected: "border-button-secondary bg-button-secondary font-medium text-text-base hover:bg-button-secondary/90 hover:text-text-base",
    unselected: "border-border-input bg-transparent text-text-primary hover:bg-background-secondary hover:text-text-primary",
  },
};

export const SegmentedControl = ({ options = [], value, onChange, disabled = false, ariaLabel, variant = "default", className = "" }) => {
  const look = VARIANTS[variant] ?? VARIANTS.default;

  return (
    <div role="radiogroup" aria-label={ariaLabel} className={cn(look.track, className)}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <ButtonShadcn
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            variant="ghost"
            size="sm"
            title={option.title}
            className={cn(look.option, selected ? look.selected : look.unselected)}
            onClick={() => {
              if (selected) return;
              onChange?.(option.value);
            }}
          >
            {option.label}
          </ButtonShadcn>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
